import { useEffect, useLayoutEffect, useState } from "react"
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom"
import { motion } from "framer-motion"
import AppLayout from "layouts/app-layout"
import Transition from "components/transition"
import moment from "moment/moment"
// import Delete from "./petty-cash/delete"
// import Filter from "./petty-cash/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import usePettyCashes from "repositories/petty-cash"
import { Pagination } from "components"
import SimplePagination from "components/simple-pagination"
import { lang } from "config"
import { ClipboardDataIcon, CloudDownloadIcon, PencilIcon, PlusIcon } from "components/icons"
import { usePage } from "contexts/page-context"
import toast from "react-hot-toast"
import axios from "axios"
import Filter from "./filter"

export default function List({ title }) {
    // Logged in user ability
    const { user } = useAuth()
    // Configuration
    const { configuration } = usePage()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const transactionNumber = searchParams.get('transaction_number')
    const transactionType = searchParams.get('transaction_type')
    const currency = searchParams.get('currency')
    const grandTotal = searchParams.get('grand_total')
    const branch = searchParams.getAll('branch_id[]')
    const source = searchParams.get('source')
    const from = searchParams.get('from') ?? moment().format('Y-MM-DD')
    const to = searchParams.get('to') ?? moment().format('Y-MM-DD')

    // Petty cash data
    const { data: pettyCashes, isLoading, mutate } = usePettyCashes({
        // branch: isSuper() ? undefined : currentBranch.id,
        page,
        search,
        transaction_number: transactionNumber,
        transaction_type: transactionType,
        branch,
        currency,
        grand_total: grandTotal,
        source,
        from,
        to,
        with: "details"
    })

    useEffect(() => {
        window.history.replaceState({}, document.title)
    }, [])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoading) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [pettyCashes, isLoading])

    const [prevRouteState, setPrevRouteState] = useState({})

    useLayoutEffect(() => {
        const getPrevRouteState = async () => {
            setPrevRouteState({
                back: location.state?.back,
                transition: location.state?.transition
            })
        }

        getPrevRouteState()
    }, [])

    const sendData = (type) => {
        const timestamp = new Date().toISOString()

        toast.loading("Loading", {
            id: `toast-${timestamp}`
        })

        axios.post(`${process.env.REACT_APP_BACKEND_URL}/petty-cash/export`, {
            type,
            transaction_type: transactionType,
            branch,
            source,
            currency,
            from,
            to,
            grand_total: grandTotal,
            phone: user?.phone
        }).then((response) => {
            toast.success(response.data.message, {
                id: `toast-${timestamp}`
            })
        }).catch((error) => {
            toast.error(error.response.data.message, {
                id: `toast-${timestamp}`
            })
        })
    }

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    return (
        <AppLayout title={title}>
            <div className="p-4 space-y-16 lg:p-8">
                {/* <div>
                    <button className="cursor-default">
                        <motion.h1 layout transition={{ type: "spring", damping: 20 }} layoutId={title} className="text-3xl font-medium">
                            {title}
                        </motion.h1>
                    </button>
                </div> */}
                <div>
                    {prevRouteState?.back && (
                        <button>
                            <Link to={location.state?.from} state={{ back: null, from: location.pathname, transition: 'slide' }} className="transition hover:opacity-50">
                                <motion.h3 layout transition={{ type: "spring", damping: 20 }} layoutId={prevRouteState?.back} className="text-sm">{prevRouteState?.back}</motion.h3>
                            </Link>
                        </button>
                    )}
                    <Transition type="slide" reversed>
                        <h1 className="text-3xl font-medium">
                            {title}
                        </h1>
                    </Transition>
                </div>
                <Transition type={location.state?.transition} reversed>
                    <div className="space-y-6">
                        <div className="flex items-center justify-between text-xs">
                            <div className="flex items-center sm:space-x-2">
                                <div className="relative hidden sm:block">
                                    <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                            <circle cx={10} cy={10} r={7}></circle>
                                            <line x1={21} y1={21} x2={15} y2={15}></line>
                                        </svg>
                                    </div>
                                    <input onChange={(e) => setSearchParams({ search: e.target.value })} value={searchParams.get('search')} type="text" placeholder={`${lang.search} ${lang.petty_cash}`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                                </div>
                                <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ transactionNumber, transactionType, currency, grandTotal, branch, source, from, to }).filter(([_, v]) => (v != null && v.length)))} />
                            </div>
                            <div className="flex items-center space-x-2">
                                <button onClick={() => sendData('xlsx')} className={`bg-neutral-800 inline-flex items-center px-4 py-3 space-x-2 text-white transition rounded-xl active:hover:scale-90`}>
                                    <CloudDownloadIcon className="w-4 h-4" strokeWidth={1.5} />
                                    <span>Export XLSX</span>
                                </button>
                            </div>
                        </div>
                        <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                            <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                                <thead className="bg-neutral-50 rounded-t-3xl">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.created_at}</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.updated_at}</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.transaction_number}</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.transaction_date}</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">No. COA Sumber Dana</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Sumber Dana</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">No. COA Tujuan</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Tujuan</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Catatan</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Tipe Transaksi</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Currency</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Debit</th>
                                        <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Credit</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-neutral-200">
                                    {/* When loading */}
                                    {isLoading && (
                                        <tr className="text-center">
                                            <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                {lang.loading_data}
                                            </td>
                                        </tr>
                                    )}

                                    {/* When there are no list available */}
                                    {pettyCashes?.data.length === 0 && !search && !isLoading && (
                                        <tr className="text-center">
                                            <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                {lang.no_data}
                                            </td>
                                        </tr>
                                    )}

                                    {/* When there are no list available on searching */}
                                    {pettyCashes?.data.length === 0 && search && !isLoading && (
                                        <tr className="text-center">
                                            <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                {lang.no_result}
                                            </td>
                                        </tr>
                                    )}

                                    {pettyCashes?.data.length > 0 && pettyCashes.data.map((row) => (
                                        <>
                                            {
                                                row.details.map((detail, index) => (
                                                    <tr>
                                                        <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                                            {index === 0 ? moment(row.created_at).format('YYYY-MM-DD H:m:ss') : ''}
                                                        </td>
                                                        <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                                            {index === 0 ? moment(row.updated_at).format('YYYY-MM-DD H:m:ss') : ''}
                                                        </td>
                                                        <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                                            {index === 0 ? row.transaction_number : ''}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {moment(detail.transaction_date).format('YYYY-MM-DD H:m:ss')}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {index === 0 ? row.source : ''}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {index === 0 ? row?.source_coa.description : ''}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {detail.destination}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {detail.destination_coa.description}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {detail.note}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {index === 0 ? row.transaction_type === 2 ? lang.internal_transfer : (row.transaction_type === 1 ? lang.expense : lang.income) : ''}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {row.status}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {row.currency}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {row.transaction_type === 0 ? Intl.NumberFormat('id-Id', { style: 'currency', currency: row.currency }).format(detail.subtotal) : ''}
                                                        </td>
                                                        <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                                            {row.transaction_type === 1 ? Intl.NumberFormat('id-Id', { style: 'currency', currency: row.currency }).format(detail.subtotal) : ''}
                                                        </td>
                                                    </tr>
                                                ))
                                            }
                                        </>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <Pagination links={pettyCashes?.links} from={pettyCashes?.from} to={pettyCashes?.to} total={pettyCashes?.total} />
                    </div>
                </Transition>
            </div>
        </AppLayout>
    )
}